import { actionTypes, StringKeyValue } from '../actions/types';
import { TrafficLight } from '../generated-types';
import ValidationFields from '../util/validation/ValidationFields';
import { asIntString, containsUint, isValidPin } from '../util/validation/validators';

type AppActions = import('../actions/types').AppActions;

export interface ApplicationReducer {
    text: StringKeyValue;
    sakId: number | undefined;
    previewCaseId: number | undefined;
    previewCaseStatus: string;
    valid: boolean;
    fornavn: string;
    epost: string;
    mobiltelefon: string;
    sivilstatus: string;
    boforhold: string;
    arbeidsforhold: string;
    annensikretgjeld: string;
    harAndreKort: string;
    kredittbelop: any;
    kredittgrense: number;
    forfallsdato: string;
    status: string;
    kortsaldo: number;
    paagaaendeSoknadBreg: number | undefined;
    pinkode: string;
    errors: any;
    creditFrozen: boolean;
    skipSbl: boolean;
    requestedCreditAmount: string;
    trafficLight?: TrafficLight;
    custom: {
        agricard: {
            agrolID?: number | undefined;
            epost?: string;
            etternavn?: string;
            fornavn?: string;
            kortType?: string;
            medlemsType?: string;
            mobil?: string;
            organisasjon?: string;
            organisasjonsnummer?: number | undefined;
            produsentnummer?: number | undefined;
            selskap?: string;
        } | null;
        agricardHash: string;
    };
}

export const initialState: ApplicationReducer = {
    text: {},
    sakId: undefined,
    previewCaseId: undefined,
    previewCaseStatus: '',
    valid: false,
    fornavn: '',
    epost: '',
    mobiltelefon: '',
    sivilstatus: '1',
    boforhold: '1',
    arbeidsforhold: '1',
    annensikretgjeld: '',
    harAndreKort: '',
    kredittbelop: undefined,
    kredittgrense: 0,
    forfallsdato: '2',
    status: '',
    kortsaldo: 0,
    paagaaendeSoknadBreg: undefined,
    pinkode: '',
    creditFrozen: false,
    skipSbl: true,
    requestedCreditAmount: '',
    trafficLight: undefined,
    errors: {
        requestedCreditAmount: '',
        epost: '',
        mobiltelefon: '',
        annensikretgjeld: '',
        harAndreKort: '',
    },
    custom: {
        agricard: null,
        agricardHash: '',
    },
};

export const applicationReducer = (state = initialState, action: AppActions = {} as AppActions): ApplicationReducer => {
    switch (action.type) {
        case actionTypes.LOAD_CONFIG: {
            const { text } = action.payload.config.data;
            return {
                ...state,
                text,
            };
        }

        case actionTypes.PARAM_STATE_UPDATE: {
            const { agricard } = action.payload;

            if (agricard && agricard.length > 0) {
                return {
                    ...state,
                    custom: {
                        ...state.custom,
                        agricardHash: agricard,
                    },
                };
            }
            return state;
        }

        case actionTypes.FETCH_AGRI_MEMBER_FULFILLED: {
            const { payload } = action;
            if (payload) {
                return {
                    ...state,
                    fornavn: payload.fornavn || state.fornavn,
                    epost: payload.epost || state.epost,
                    mobiltelefon: payload.mobil.length === 8 ? payload.mobil : state.mobiltelefon,
                    custom: {
                        ...state.custom,
                        agricard: payload,
                    },
                };
            }
            return state;
        }

        case actionTypes.FETCH_USER_CONTEXT_FULFILLED: {
            return {
                ...state,
                fornavn: state.fornavn || action.payload.fornavn || '',
                epost: state.epost || action.payload.epost || '',
                mobiltelefon: state.mobiltelefon || action.payload.telefon || '',
            };
        }

        case actionTypes.FETCH_STATUS_FULFILLED: {
            const { response } = action.payload;
            const { status, bankreg, sakId, innvilgetKredittgrense, kort, creditFrozen, trafficLight, skipSbl } =
                response;
            return {
                ...state,
                skipSbl: skipSbl || state.skipSbl,
                status: status || state.status,
                sakId: sakId ? Number(sakId) : state.sakId,
                paagaaendeSoknadBreg: Number(bankreg) || state.paagaaendeSoknadBreg,
                kredittgrense: innvilgetKredittgrense || state.kredittgrense,
                kredittbelop: String(innvilgetKredittgrense) || state.kredittbelop,
                kortsaldo: (kort && kort.saldo) || state.kortsaldo,
                creditFrozen: creditFrozen || state.creditFrozen,
                trafficLight: trafficLight || state.trafficLight,
            };
        }

        case actionTypes.FETCH_CASE_FULFILLED: {
            const { status, bankreg, sakId, innvilgetKredittgrense, kort, creditFrozen, skipSbl, trafficLight } =
                action.payload;
            return {
                ...state,
                skipSbl: skipSbl || state.skipSbl,
                status: status || state.status,
                sakId: sakId ? Number(sakId) : state.sakId,
                paagaaendeSoknadBreg: Number(bankreg) || state.paagaaendeSoknadBreg,
                kredittgrense: innvilgetKredittgrense,
                kredittbelop: String(innvilgetKredittgrense) || state.kredittbelop,
                kortsaldo: (kort && kort.saldo) || state.kortsaldo,
                creditFrozen: creditFrozen || state.creditFrozen,
                trafficLight: trafficLight || state.trafficLight,
            };
        }

        /* Send Application (1) */
        case actionTypes.SEND_APPLICATION_FULFILLED: {
            const { innvilgetKredittgrense, preViewSakId, status, sakId, eksisterendeKontoBalanse, creditFrozen } =
                action.payload;
            return {
                ...state,
                sakId: Number(sakId),
                previewCaseId: Number(preViewSakId),
                kredittgrense: innvilgetKredittgrense,
                kortsaldo: eksisterendeKontoBalanse || state.kortsaldo,
                status,
                creditFrozen,
            };
        }

        /* Send Order (2) */
        case actionTypes.SEND_ORDER_FULFILLED: {
            return {
                ...state,
                status: 'TIL_SIGNERING',
            };
        }

        /*  Complete Order (3) */
        case actionTypes.COMPLETE_ORDER_FULFILLED: {
            return {
                ...state,
                status: 'SIGNERT',
            };
        }

        case actionTypes.CHANGE_SOKNAD_FIELD: {
            const { name, value } = action.payload;
            /* Default State */
            const newState = {
                ...state,
                [name]: value,
                errors: {
                    ...state.errors,
                    formError: '',
                },
            };

            /* Custom Fields */
            switch (name) {
                case ValidationFields.TELEFONNUMMER: {
                    const telefonnummer = asIntString(value);
                    if (containsUint(telefonnummer) || telefonnummer === '') {
                        return {
                            ...state,
                            mobiltelefon: telefonnummer,
                            errors: {
                                ...state.errors,
                                mobiltelefon: '',
                            },
                        };
                    }
                    return {
                        ...state,
                    };
                }
                case ValidationFields.EPOSTADRESSE: {
                    return {
                        ...newState,
                    };
                }
                case ValidationFields.SIKRET_GJELD: {
                    const sikretGjeld = asIntString(value);
                    if (containsUint(sikretGjeld) || sikretGjeld === '') {
                        return {
                            ...state,
                            annensikretgjeld: sikretGjeld,
                            errors: {
                                ...state.errors,
                                annensikretgjeld: '',
                            },
                        };
                    }
                    return {
                        ...state,
                    };
                }
                case ValidationFields.ANDREKORT: {
                    return {
                        ...newState,
                        harAndreKort: value,
                        errors: {
                            ...newState.errors,
                            harAndreKort: '',
                        },
                    };
                }
                case ValidationFields.BOFORHOLD: {
                    return {
                        ...newState,
                        boforhold: value,
                        errors: {
                            ...newState.errors,
                            boforhold: '',
                        },
                    };
                }
                case ValidationFields.FORFALLSDATO: {
                    return {
                        ...newState,
                        forfallsdato: value,
                        errors: {
                            ...newState.errors,
                            forfallsdato: '',
                        },
                    };
                }

                default:
                    return newState;
            }
        }
        case actionTypes.VERIFY_SOKNAD_FIELD: {
            const { value, name } = action.payload;
            const { text } = state;
            let errors = { ...state.errors };
            switch (name) {
                case ValidationFields.CREDITAMOUNT: {
                    const parsedCreditAmount = asIntString(value);
                    return {
                        ...state,
                        requestedCreditAmount: parsedCreditAmount,
                        errors: {
                            ...state.errors,
                            requestedCreditAmount: containsUint(parsedCreditAmount) ? '' : text.errorEmptyCreditAmount,
                        },
                    };
                }
                case ValidationFields.TELEFONNUMMER: {
                    const telefonnummer = asIntString(value);

                    let error = '';
                    if (!telefonnummer) {
                        error = text.errorEmptyMobilnummer;
                    } else if (telefonnummer.length !== 8) {
                        error = text.errorMobiltelefon;
                    }

                    return {
                        ...state,
                        mobiltelefon: telefonnummer,
                        errors: {
                            ...state.errors,
                            mobiltelefon: error,
                        },
                    };
                }
                case ValidationFields.EPOSTADRESSE: {
                    // eslint-disable-next-line max-len
                    const regExp =
                        /^(([^<>()[\]\\.,;:\s@æÆøØåÅ"]+(\.[^<>()[\]\\.,;:\s@æÆøØåÅ"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    const hasNorwegianDomainLetters =
                        value.includes('xn--') ||
                        value.includes('æ') ||
                        value.includes('Æ') ||
                        value.includes('ø') ||
                        value.includes('Ø') ||
                        value.includes('å') ||
                        value.includes('Å');
                    if (!regExp.test(value) || hasNorwegianDomainLetters) {
                        errors = {
                            ...state.errors,
                            epost: text.errorEmail,
                        };
                    } else {
                        errors = {
                            ...state.errors,
                            epost: '',
                        };
                    }
                    break;
                }
                case ValidationFields.SIKRET_GJELD: {
                    const sikretGjeld = asIntString(value);
                    return {
                        ...state,
                        annensikretgjeld: sikretGjeld,
                        errors: {
                            ...state.errors,
                            annensikretgjeld: containsUint(sikretGjeld) ? '' : text.errorEmptySikretGjeld,
                        },
                    };
                }
                case ValidationFields.KREDITTBELOP: {
                    const { kortsaldo, kredittbelop } = state;
                    const saldo = kortsaldo * -1;
                    const validCredit = kredittbelop > saldo;
                    return {
                        ...state,
                        kredittbelop: value,
                        errors: {
                            ...state.errors,
                            kredittbelop: validCredit ? '' : text.errorInvalidCredit,
                        },
                    };
                }
                case ValidationFields.PIN_KODE: {
                    const { value } = action.payload;
                    return {
                        ...state,
                        pinkode: value,
                        errors: {
                            ...state.errors,
                            pinkode: isValidPin(value) ? '' : text.summarySetPinTooltip,
                        },
                    };
                }
                case ValidationFields.PIN_KODE_CONFIRM: {
                    const { value } = action.payload;
                    const valid = state.pinkode === value;
                    return {
                        ...state,
                        errors: {
                            ...state.errors,
                            pinConfirm: valid ? '' : text.errorInvalidPinConfirm,
                        },
                    };
                }
                case ValidationFields.CUSTOM_PIN_KODE: {
                    return {
                        ...state,
                        errors: {
                            ...state.errors,
                            pinkode: '',
                            pinConfirm: '',
                        },
                    };
                }

                default: {
                    break;
                }
            }
            return {
                ...state,
                errors,
            };
        }
        case actionTypes.VERIFY_CONTACT_INFO: {
            let valid = true;
            const errorList = { ...state.errors, formError: '' };
            const { text, epost, mobiltelefon } = state;

            if (!epost) errorList.epost = text.errorEmptyEmail;
            if (!mobiltelefon) errorList.mobiltelefon = text.errorEmptyMobilnummer;

            for (const key in errorList) {
                if (Object.prototype.hasOwnProperty.call(errorList, key)) {
                    if (errorList[key]) {
                        valid = false;
                    }
                }
            }
            if (!valid) errorList.formError = text.errorCheckForm;

            return {
                ...state,
                valid,
                errors: errorList,
            };
        }
        case actionTypes.VERIFY_SOKNAD_SKJEMA: {
            let valid = true;
            const errorList = { ...state.errors, formError: '' };

            const { text, epost, mobiltelefon, annensikretgjeld, harAndreKort } = state;

            if (!epost) errorList.epost = text.errorEmptyEmail;
            if (!mobiltelefon) errorList.mobiltelefon = text.errorEmptyMobilnummer;
            if (!annensikretgjeld) errorList.annensikretgjeld = text.errorEmptySikretGjeld;
            if (!harAndreKort) errorList.harAndreKort = text.errorEmptyHarAndreKort;

            for (const key in errorList) {
                if (Object.prototype.hasOwnProperty.call(errorList, key)) {
                    if (errorList[key]) {
                        valid = false;
                    }
                }
            }

            if (!valid) {
                errorList.formError = text.errorCheckForm;
            }
            return {
                ...state,
                valid,
                errors: errorList,
            };
        }
        case actionTypes.VERIFY_BESTILLING_SKJEMA: {
            let valid = true;
            const errorList = { ...state.errors, formError: '' };
            const { text } = state;

            for (const key in errorList) {
                if (Object.prototype.hasOwnProperty.call(errorList, key)) {
                    if (errorList[key]) {
                        valid = false;
                    }
                }
            }
            if (!valid) errorList.formError = text.errorCheckForm;

            return {
                ...state,
                valid,
                errors: errorList,
            };
        }

        default:
            return state;
    }
};
